import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, BarChart3 } from "lucide-react";
import { StatCard } from "./shared";

export default function AnalyticsTab() {
  const [range, setRange] = useState("month");
  const [overview, setOverview] = useState(null);
  const [pages, setPages] = useState([]);
  const [sources, setSources] = useState({ paid: 0, organic: 0, referral: 0, direct: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const [{ data: o }, { data: p }, { data: s }] = await Promise.all([
          api.get(`/analytics/overview?range=${range}`),
          api.get(`/analytics/pages?range=${range}`),
          api.get(`/analytics/traffic-sources?range=${range}`),
        ]);
        if (!alive) return;
        setOverview(o); setPages(p.pages || []); setSources(s.sources || {});
      } catch { if (alive) toast.error("Couldn't load analytics"); }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [range]);

  const RANGES = [
    { id: "today", label: "Today" }, { id: "week", label: "7 days" },
    { id: "month", label: "30 days" }, { id: "year", label: "Year" },
  ];

  const fmt$ = (cents) =>
    `$${((cents || 0) / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div data-testid="analytics-tab">
      <div className="nb-card p-5 mb-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-display text-2xl font-extrabold flex items-center gap-2"><BarChart3 className="w-6 h-6" /> Analytics</div>
          <div className="text-sm text-gray-600 mt-1">See what&apos;s working on your website.</div>
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {RANGES.map((r) => (
            <button key={r.id} onClick={() => setRange(r.id)} data-testid={`range-${r.id}`}
              className={`px-3 py-1.5 rounded-full text-xs font-bold border-2 border-black ${range === r.id ? "bg-[#111] text-white" : "bg-white"}`}>
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="nb-card-flat p-8 text-center"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
      ) : (
        <>
          <div className="grid sm:grid-cols-4 gap-3 mb-6">
            <StatCard label="Visits" value={overview?.visits || 0} testId="stat-visits" />
            <StatCard label="Leads" value={overview?.leads || 0} testId="stat-leads" />
            <StatCard label="Sales" value={overview?.sales || 0} testId="stat-sales" />
            <StatCard label="Revenue" value={fmt$(overview?.revenue_cents)} testId="stat-revenue" />
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <div className="nb-card p-5">
              <div className="text-[10px] uppercase tracking-widest font-extrabold text-gray-500 mb-3">Top pages</div>
              {pages.length === 0 ? (
                <div className="text-sm text-gray-600 py-6 text-center">No visits yet in this range.</div>
              ) : (
                <div className="space-y-2" data-testid="top-pages-list">
                  {pages.slice(0, 10).map((p) => {
                    const max = pages[0]?.visits || 1;
                    const pct = Math.round((p.visits / max) * 100);
                    return (
                      <div key={p.page} data-testid={`page-row-${(p.page || "/").replace(/[^a-z0-9]/gi, "-")}`}>
                        <div className="flex justify-between text-sm">
                          <span className="font-semibold truncate max-w-[60%]">{p.page}</span>
                          <span className="font-extrabold">{p.visits}</span>
                        </div>
                        <div className="mt-1 h-2 rounded-full bg-gray-100 overflow-hidden">
                          <div className="h-full bg-[#8B5CF6]" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="nb-card p-5">
              <div className="text-[10px] uppercase tracking-widest font-extrabold text-gray-500 mb-3">Traffic sources</div>
              <div className="space-y-3" data-testid="sources-list">
                {[
                  { id: "organic", label: "Organic search", color: "#10B981" },
                  { id: "paid", label: "Paid ads", color: "#F59E0B" },
                  { id: "referral", label: "Referral", color: "#3B82F6" },
                  { id: "direct", label: "Direct", color: "#6B7280" },
                ].map((s) => {
                  const v = sources[s.id] || 0;
                  const total = Object.values(sources).reduce((a, b) => a + b, 0) || 1;
                  const pct = Math.round((v / total) * 100);
                  return (
                    <div key={s.id} data-testid={`source-${s.id}`}>
                      <div className="flex justify-between text-sm">
                        <span className="font-semibold flex items-center gap-2">
                          <span className="w-3 h-3 rounded-full" style={{ background: s.color }} />{s.label}
                        </span>
                        <span className="font-extrabold">{v} · {pct}%</span>
                      </div>
                      <div className="mt-1 h-2 rounded-full bg-gray-100 overflow-hidden">
                        <div className="h-full" style={{ width: `${pct}%`, background: s.color }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="nb-card-flat p-4 mt-6 text-sm bg-[#F0FDF4]">
            <b>Tip:</b> Share links with UTM parameters (e.g. <code>?utm_medium=cpc&utm_source=google_ads</code>) so we can correctly attribute paid traffic.
          </div>
        </>
      )}
    </div>
  );
}
